import axios from "axios";
import { useCallback, useEffect, useState } from "react";

interface UploadState {
    fileId?: string;
    file: File;
    loaded?: number;
    total?: number;
    status: "waiting" | "uploading" | "success" | "error";
    error?: string;
    abortController?: AbortController;
}

export const useFileUploader = (
    createUpload: (filename: string) => Promise<{
        id: string;
        uploadUrl: string;
    }>,
    markUploadAsComplete: (fileId: string) => Promise<void>,
    deleteUpload: (fileId: string) => Promise<void>,
    uploadCompleteCallback?: () => void,
) => {
    // State tracking for uploads
    const [uploadState, setUploadState] = useState<{
        [key: string]: UploadState;
    }>({});

    // Add files ignoring any duplicates
    const addFiles = useCallback(
        (newFiles: File[]) => {
            const newUploadState = { ...uploadState };
            newFiles.forEach((f) => {
                if (newUploadState[f.name] === undefined) {
                    newUploadState[f.name] = {
                        file: f,
                        status: "waiting",
                        abortController: new AbortController(),
                    };
                }
            });
            setUploadState(newUploadState);
        },
        [uploadState, setUploadState],
    );

    // Remove a file
    const removeFile = useCallback(
        (filename: string) => {
            const newUploadState = { ...uploadState };
            const fileId = newUploadState[filename].fileId;
            newUploadState[filename].abortController.abort();
            delete newUploadState[filename];
            setUploadState(newUploadState);
            // Handle deleting on server
            if (fileId) {
                deleteUpload(fileId);
            }
        },
        [uploadState, setUploadState, deleteUpload],
    );

    // Update state of an item (internal)
    const updateState = useCallback(
        (filename: string, data: Partial<UploadState>) => {
            setUploadState((prevState) => {
                const newUploadState = { ...prevState };
                newUploadState[filename] = {
                    ...newUploadState[filename],
                    ...data,
                };
                return newUploadState;
            });
        },
        [setUploadState],
    );

    // Download file (internal)
    const downloadFile = useCallback(
        async (state: UploadState) => {
            updateState(state.file.name, {
                status: "uploading",
            });
            try {
                // Retrieve presigned URL
                const file = await createUpload(state.file.name);

                // Update internal state
                updateState(state.file.name, {
                    fileId: file.id,
                });

                // Upload file and update upload status
                await axios.put(file.uploadUrl, state.file, {
                    headers: {
                        "Content-Type": state.file.type,
                    },
                    onUploadProgress: (e) =>
                        updateState(state.file.name, {
                            status: "uploading",
                            total: e.total,
                            loaded: e.loaded,
                        }),
                    signal: state.abortController.signal,
                });

                // Mark upload as complete
                await markUploadAsComplete(file.id);

                // Update state
                updateState(state.file.name, {
                    loaded: undefined,
                    total: undefined,
                    status: "success",
                });
                if (uploadCompleteCallback) {
                    uploadCompleteCallback();
                }
            } catch (e) {
                // Ignore abort errors
                if (e.code === "ERR_CANCELED") {
                    return;
                }
                // Else, try parsing error
                let error = "Could not complete request.";
                try {
                    const errorResponse = await e.response.json();
                    error = errorResponse.file[0];
                } catch {
                    console.log("Failed to parse error message body.");
                }
                updateState(state.file.name, {
                    loaded: undefined,
                    total: undefined,
                    status: "error",
                    error: error,
                });
            }
        },
        [
            updateState,
            createUpload,
            markUploadAsComplete,
            uploadCompleteCallback,
        ],
    );

    // Start upload process as soon as there are new items
    useEffect(() => {
        // Loop through all items
        for (const filename in uploadState) {
            const state = uploadState[filename];

            // Check if they are waiting upload
            if (state.status === "waiting") {
                downloadFile(state);
            }
        }
    }, [uploadState, downloadFile]);

    return {
        addFiles,
        removeFile,
        uploadState,
    };
};
